import {
  Box,
  Button,
  CircularProgress,
  Typography,
  Grid2,
} from '@mui/material';

import { Form, Formik, FormikHelpers } from 'formik';
import { useState } from 'react';
import { isAxiosError } from 'axios';
import useAxiosPrivate from '../../../../hooks/useAxiosPrivate';
import { InputField } from '../FormFields';
import validationSchema from './validationSchema';
import { Modal } from '../../../../global';

interface FormikValues {
  username: string;
  password: string;
  confirmPassword: string;
  firstName: string;
  lastName: string;
  email: string;
}

const CreateUserForm = () => {
  const axiosPrivate = useAxiosPrivate();
  const [showModal, setShowModal] = useState(false);
  const [modalInfo, setModalInfo] = useState({
    titleText: '',
    bodyText: '',
    autoClose: false,
  });

  const createUser = async ({
    username,
    password,
    firstName,
    lastName,
    email,
  }: {
    username: string;
    password: string;
    firstName: string;
    lastName: string;
    email: string;
  }) => {
    try {
      const response = await axiosPrivate.post('/create-user', {
        user: username,
        pwd: password,
        firstName,
        lastName,
        email,
      });
      if (response.status === 201) return 'Success';
      console.error('Status was not 201', response);
      return 'Failure adding User';
    } catch (error: unknown) {
      if (isAxiosError(error)) {
        if (error.response?.status === 409) return 'User already Exist';
        return error.response?.data;
      }
      return console.error('Unexpect Error', error);
    }
  };

  const handleCreateUserResponse = (
    axiosResponse: string,
    values: FormikValues,
    actions: FormikHelpers<FormikValues>
  ) => {
    if (axiosResponse === 'Success') {
      actions.resetForm();
      setModalInfo({
        titleText: 'Success!',
        bodyText: `User: ${values.username} was created!`,
        autoClose: true,
      });
      setShowModal(true);
    }
    if (axiosResponse === 'User already Exist') {
      setModalInfo({
        titleText: 'Duplicate Username!',
        bodyText: `Username: ${values.username} already Exist!`,
        autoClose: true,
      });
      setShowModal(true);
    }
    if (axiosResponse === 'Failure adding User') {
      setModalInfo({
        titleText: 'Failure adding User!',
        bodyText:
          'An Issue was encouter while uploading user! Please try again',
        autoClose: false,
      });
      setShowModal(true);
    }
  };

  const handleSubmit = async (
    values: FormikValues,
    actions: FormikHelpers<FormikValues>
  ) => {
    const axiosResponse = await createUser(values);

    handleCreateUserResponse(axiosResponse as string, values, actions);

    actions.setTouched({});
    actions.setSubmitting(false);
  };

  return (
    <>
      <Typography
        component="h1"
        variant="h4"
        align="center"
        sx={{ mb: '0.5em' }}
      >
        Create User
      </Typography>
      {showModal && (
        <Modal
          titleText={modalInfo.titleText}
          bodyText={modalInfo.bodyText}
          autoClose={modalInfo.autoClose}
          updateParent={setShowModal}
        />
      )}
      <Formik
        initialValues={{
          username: '',
          password: '',
          confirmPassword: '',
          firstName: '',
          lastName: '',
          email: '',
        }}
        validationSchema={validationSchema}
        onSubmit={handleSubmit}
      >
        {({ isSubmitting }) => (
          <Form id="createUserForm">
            <Grid2
              container
              spacing={3}
              alignItems="center"
              justifyContent="center"
            >
              <Grid2 size={{ xs: 12, sm: 6 }}>
                <InputField
                  name="firstName"
                  label="First Name"
                  textFieldType="text"
                  fullWidth
                />
              </Grid2>
              <Grid2 size={{ xs: 12, sm: 6 }}>
                <InputField
                  name="lastName"
                  label="Last Name"
                  textFieldType="text"
                  fullWidth
                />
              </Grid2>
              <Grid2 size={{ xs: 12 }}>
                <InputField
                  name="username"
                  label="Username"
                  textFieldType="text"
                  fullWidth
                />
              </Grid2>
              <Grid2 size={{ xs: 12 }}>
                <InputField
                  name="email"
                  label="Email"
                  textFieldType="text"
                  fullWidth
                />
              </Grid2>
              <Grid2 size={{ xs: 12, sm: 6 }}>
                <InputField
                  name="password"
                  label="Password"
                  textFieldType="password"
                  fullWidth
                />
              </Grid2>
              <Grid2 size={{ xs: 12, sm: 6 }}>
                <InputField
                  name="confirmPassword"
                  label="Confirm Password"
                  textFieldType="password"
                  fullWidth
                />
              </Grid2>
            </Grid2>
            <Box sx={{ display: 'flex', justifyContent: 'end', marginTop: 2 }}>
              <Box>
                <Button
                  disabled={isSubmitting}
                  type="submit"
                  variant="contained"
                  color="primary"
                  sx={{ marginLeft: 2 }}
                >
                  Create User
                </Button>
                {isSubmitting && <CircularProgress size={24} />}
              </Box>
            </Box>
          </Form>
        )}
      </Formik>
    </>
  );
};

export default CreateUserForm;
